import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'
import { videos } from '../data/mockData'
import { Play, X } from 'lucide-react'

const VideoGallery = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-100px' })
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [playingVideos, setPlayingVideos] = useState({})

  // Références pour chaque vidéo
  const videoRefs = useRef([])

  // Observer pour détecter quand une vidéo est visible
  useEffect(() => {
    const observers = []

    videoRefs.current.forEach((videoRef, index) => {
      if (!videoRef) return

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Démarrer la vidéo quand elle est visible
              setPlayingVideos(prev => ({ ...prev, [index]: true }))
            } else {
              // Arrêter la vidéo quand elle n'est plus visible
              setPlayingVideos(prev => ({ ...prev, [index]: false }))
            }
          })
        },
        {
          threshold: 0.5, // 50% de la vidéo visible
          rootMargin: '0px 0px -100px 0px' // Démarrer un peu avant d'arriver complètement
        }
      )

      observer.observe(videoRef)
      observers.push(observer)
    })

    // Nettoyage
    return () => {
      observers.forEach(observer => observer.disconnect())
    }
  }, [videos])

  return (
    <section id="videos" className="py-20 bg-gradient-to-br from-purple-50 to-white" ref={ref}>
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">Galerie Vidéo</h2>
          <p className="section-subtitle max-w-3xl mx-auto">
            Découvrez nos méthodes et témoignages en vidéo
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {videos.map((video, i) => (
            <motion.div
              key={video.id}
              ref={el => videoRefs.current[i] = el}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="group relative bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer card-hover"
              onClick={() => setSelectedVideo(video)}
            >
              {/* Version avec lecture automatique */}
              {playingVideos[i] ? (
                <div className="relative h-56 overflow-hidden">
                  <iframe
                    src={`${video.videoUrl}?autoplay=1&mute=1&controls=1`}
                    className="w-full h-full object-cover"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                  <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-xs font-medium">
                    {video.duration}
                  </div>
                </div>
              ) : (
                // Version miniature avec bouton play
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors duration-300 flex items-center justify-center">
                    <motion.div
                      className="w-16 h-16 bg-white rounded-full flex items-center justify-center"
                      whileHover={{ scale: 1.2 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Play className="w-8 h-8 text-primary ml-1" />
                    </motion.div>
                  </div>
                  <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-xs font-medium">
                    {video.duration}
                  </div>
                </div>
              )}

              {/* Informations de la vidéo */}
             
            </motion.div>
          ))}
        </div>
      </div>

      {selectedVideo && (
        <motion.div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedVideo(null)}
        >
          <motion.div
            className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden"
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
              onClick={() => setSelectedVideo(null)}
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>
            <div className="aspect-video bg-black">
              <iframe
                src={`${selectedVideo.videoUrl}?autoplay=1`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
           
          </motion.div>
        </motion.div>
      )}
    </section>
  )
}

export default VideoGallery